/**
 * TelemetryInsightCard
 *
 * Displays a single telemetry insight (fatigue warning, plateau alert, etc.)
 * returned by trpc.telemetry.getInsights.
 *
 * Variants:
 *   fatigue_warning    — amber/red, ⚠️ icon
 *   overload_plateau   — orange, 🧱 icon
 *   hypertrophy_plateau — blue, 🧱 icon
 *   frequency_gap      — yellow, 📅 icon
 *   deload_due         — purple, 🔄 icon
 *   positive           — green, ✅ icon
 */

import { AlertTriangle, BrickWall, Calendar, RefreshCw, CheckCircle2, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface TelemetryInsight {
  type:
    | "fatigue_warning"
    | "overload_plateau"
    | "hypertrophy_plateau"
    | "frequency_gap"
    | "deload_due"
    | "positive";
  severity: "high" | "medium" | "low";
  title: string;
  body: string;
  action?: string;
}

interface TelemetryInsightCardProps {
  insight: TelemetryInsight;
  /** If true, renders a compact single-line version for dense layouts */
  compact?: boolean;
  className?: string;
}

const VARIANT_CONFIG: Record<
  TelemetryInsight["type"],
  {
    Icon: React.ElementType;
    borderColor: string;
    bgColor: string;
    iconColor: string;
    badgeColor: string;
    badgeText: string;
  }
> = {
  fatigue_warning: {
    Icon: AlertTriangle,
    borderColor: "border-red-500/40",
    bgColor: "bg-red-950/30",
    iconColor: "text-red-400",
    badgeColor: "bg-red-500/20 text-red-300",
    badgeText: "⚠ FATIGUE DETECTED",
  },
  overload_plateau: {
    Icon: BrickWall,
    borderColor: "border-orange-500/40",
    bgColor: "bg-orange-950/30",
    iconColor: "text-orange-400",
    badgeColor: "bg-orange-500/20 text-orange-300",
    badgeText: "🧱 OVERLOAD PLATEAU",
  },
  hypertrophy_plateau: {
    Icon: BrickWall,
    borderColor: "border-blue-500/40",
    bgColor: "bg-blue-950/30",
    iconColor: "text-blue-400",
    badgeColor: "bg-blue-500/20 text-blue-300",
    badgeText: "🧱 HYPERTROPHY PLATEAU",
  },
  frequency_gap: {
    Icon: Calendar,
    borderColor: "border-yellow-500/40",
    bgColor: "bg-yellow-950/30",
    iconColor: "text-yellow-400",
    badgeColor: "bg-yellow-500/20 text-yellow-300",
    badgeText: "📅 FREQUENCY GAP",
  },
  deload_due: {
    Icon: RefreshCw,
    borderColor: "border-purple-500/40",
    bgColor: "bg-purple-950/30",
    iconColor: "text-purple-400",
    badgeColor: "bg-purple-500/20 text-purple-300",
    badgeText: "🔄 DELOAD DUE",
  },
  positive: {
    Icon: CheckCircle2,
    borderColor: "border-green-500/40",
    bgColor: "bg-green-950/20",
    iconColor: "text-green-400",
    badgeColor: "bg-green-500/20 text-green-300",
    badgeText: "✓ RECOVERY STRONG",
  },
};

export function TelemetryInsightCard({
  insight,
  compact = false,
  className,
}: TelemetryInsightCardProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const config = VARIANT_CONFIG[insight.type];
  const { Icon } = config;

  if (compact) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border px-3 py-2 text-xs",
          config.borderColor,
          config.bgColor,
          className
        )}
      >
        <Icon className={cn("h-3.5 w-3.5 shrink-0", config.iconColor)} />
        <span className="font-medium text-white/90">{insight.title}</span>
        <span className="text-white/50 truncate">{insight.body}</span>
        <button
          onClick={() => setDismissed(true)}
          className="ml-auto shrink-0 text-white/30 hover:text-white/70 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative rounded-lg border p-4",
        config.borderColor,
        config.bgColor,
        className
      )}
    >
      {/* Dismiss button */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 text-white/30 hover:text-white/70 transition-colors"
        aria-label="Dismiss insight"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Header */}
      <div className="flex items-start gap-3 pr-6">
        <div className={cn("mt-0.5 shrink-0", config.iconColor)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          {/* Badge */}
          <span
            className={cn(
              "inline-block rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider mb-1",
              config.badgeColor
            )}
          >
            {config.badgeText}
          </span>

          {/* Title */}
          <p className="text-sm font-semibold text-white leading-snug">{insight.title}</p>

          {/* Body */}
          <p className="mt-1 text-xs text-white/60 leading-relaxed">{insight.body}</p>

          {/* Action recommendation */}
          {insight.action && (
            <div className="mt-2 flex items-start gap-1.5">
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider shrink-0 mt-0.5">
                Rec:
              </span>
              <p className="text-xs text-white/70 italic">{insight.action}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * TelemetryInsightBanner
 *
 * Renders all active insights from trpc.telemetry.getInsights.
 * High-severity insights first. Positive insights are suppressed when
 * any warning is present.
 */
interface TelemetryInsightBannerProps {
  insights: TelemetryInsight[];
  compact?: boolean;
  className?: string;
}

export function TelemetryInsightBanner({
  insights,
  compact = false,
  className,
}: TelemetryInsightBannerProps) {
  if (!insights || insights.length === 0) return null;

  // Filter out positive signals if any warning is present
  const hasWarning = insights.some((i) => i.type !== "positive");
  const filtered = hasWarning ? insights.filter((i) => i.type !== "positive") : insights;

  // Sort: high → medium → low
  const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };
  const sorted = [...filtered].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {sorted.map((insight, i) => (
        <TelemetryInsightCard key={`${insight.type}-${i}`} insight={insight} compact={compact} />
      ))}
    </div>
  );
}
